import { useState, useCallback } from 'react';
import { addGroupMembers, getAvailableFriendsForGroup } from '../services/groupService';

/**
 * Hook for managing group members.
 * Uses server-side callable for adding members.
 *
 * @param {string} groupId - The group ID
 * @param {Array} existingMembers - Current group members (for filtering available friends)
 */
const useGroupMembers = (groupId, existingMembers) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Filter friends who aren't already members of the group
   * @param {Array} friendsList - List of friends
   * @returns {Array} Friends who can be added to the group
   */
  const getAvailableFriends = useCallback(
    (friendsList) => {
      return getAvailableFriendsForGroup(friendsList, existingMembers);
    },
    [existingMembers]
  );

  /**
   * Add members to the group using server-side callable.
   * @param {Array} memberIds - Array of user IDs to add as members
   * @returns {Promise<{addedMembers: Array, addedCount: number}>} Added members with user data
   */
  const addMembers = useCallback(
    async (memberIds) => {
      if (!memberIds || memberIds.length === 0) {
        return { addedMembers: [], addedCount: 0 };
      }

      if (!groupId) {
        throw new Error('Group ID is required');
      }

      setLoading(true);
      setError(null);

      try {
        const result = await addGroupMembers(groupId, memberIds);
        return result;
      } catch (err) {
        console.error('Error adding members:', err);
        // Extract error message from Firebase callable error
        const message = err?.message || err?.code || 'Failed to add members';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [groupId]
  );

  return {
    loading,
    error,
    getAvailableFriends,
    addMembers,
  };
};

export default useGroupMembers; 